import { Capacitor } from "@capacitor/core";
import { resolveMediaUrl } from "@/lib/mediaUrl";

// Album de la galerie où Yalola range ce qu'un membre enregistre (Android : Android/media/app.yalola/Yalola).
export const GALLERY_ALBUM = "Yalola";

const EXT_BY_MIME = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif", "video/mp4": "mp4", "video/webm": "webm", "video/quicktime": "mov" };

// Nom de fichier SANS extension (Android ajoute celle du média téléchargé).
export const mediaBaseName = (kind, now = new Date()) =>
  `Yalola_${kind === "video" ? "VID" : "IMG"}_${now.toISOString().replace(/[-:T.Z]/g, "").slice(0, 17)}`;

// Extension finale : type MIME réel du blob, sinon celle de l'URL, sinon jpg / mp4.
export const mediaExt = (url, mime, kind) => {
  if (mime && EXT_BY_MIME[mime.split(";")[0]]) return EXT_BY_MIME[mime.split(";")[0]];
  const m = String(url || "").split(/[?#]/)[0].match(/\.([a-z0-9]{2,4})$/i);
  if (m) return m[1].toLowerCase();
  return kind === "video" ? "mp4" : "jpg";
};

// Ancienne app sans le plugin natif → Capacitor rejette avec UNIMPLEMENTED.
const isUnimplemented = (e) => e?.code === "UNIMPLEMENTED" || /not implemented|not available/i.test(String(e?.message || e || ""));

// Android : l'identifiant d'un album = chemin de son dossier ; plusieurs albums peuvent porter le même nom,
// on ne garde que celui du dossier média de l'app (aucune permission requise, contrairement au mode galerie).
export async function androidAlbumIdentifier(Media) {
  const { path } = await Media.getAlbumsPath();
  const { albums } = await Media.getAlbums();
  const found = (albums || []).find((a) => a.name === GALLERY_ALBUM && String(a.identifier || "").startsWith(path));
  if (found) return found.identifier;
  try { await Media.createAlbum({ name: GALLERY_ALBUM }); } catch (e) { if (!/exist/i.test(String(e?.message || ""))) throw e; }
  return `${path}/${GALLERY_ALBUM}`;
}

// Lecture du média en blob : clé de cache neuve (« dl=… ») — une copie CDN mise en cache par un <img> (sans en-tête
// Origin) n'a pas d'Access-Control-Allow-Origin et ferait échouer le fetch CORS.
export const fetchMediaBlob = async (url) => {
  const bust = `${url}${url.includes("?") ? "&" : "?"}dl=${Date.now()}`;
  const res = await fetch(bust, { mode: "cors", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};

const webDownload = async (url, kind) => {
  try {
    const blob = await fetchMediaBlob(url);
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href; a.download = `${mediaBaseName(kind)}.${mediaExt(url, blob.type, kind)}`; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 3000);
    return "downloaded";
  } catch {
    // CDN sans CORS : le navigateur ouvre le média, le membre l'enregistre depuis l'onglet.
    window.open(url, "_blank", "noopener");
    return "opened";
  }
};

// Enregistre une photo / vidéo du fil : app native → galerie du téléphone (plugin Media, façon Facebook) ;
// app sans le plugin → feuille de partage du système ; web → téléchargement du navigateur.
// Retourne "gallery" | "shared" | "cancelled" | "denied" | "downloaded" | "opened".
export async function saveMediaToGallery({ url, kind = "image" } = {}) {
  if (!url) throw new Error("saveMediaToGallery: url manquante");
  url = resolveMediaUrl(url); // lot 623 — « /api/files/… » relatif (visionneuse plein écran) → absolu, sinon l'app native ne trouve pas le fichier
  if (!Capacitor.isNativePlatform()) return webDownload(url, kind);
  try {
    const { Media } = await import("@capacitor-community/media");
    const opts = { path: url, fileName: mediaBaseName(kind) };
    if (Capacitor.getPlatform() === "android") opts.albumIdentifier = await androidAlbumIdentifier(Media);
    if (kind === "video") await Media.saveVideo(opts); else await Media.savePhoto(opts);
    return "gallery";
  } catch (e) {
    if (e?.code === "accessDenied") return "denied";
    if (!isUnimplemented(e)) throw e;
  }
  const { saveOrShareFile } = await import("@/lib/nativeShare");
  const blob = await fetchMediaBlob(url);
  const how = await saveOrShareFile({ blob, fileName: `${mediaBaseName(kind)}.${mediaExt(url, blob.type, kind)}`, title: "Yalola" });
  return how === "cancelled" ? "cancelled" : "shared";
}

// Tout l'album, l'un après l'autre (le plugin natif travaille média par média). Un refus / annulation arrête la série.
// Retourne { saved, total, how } — `how` = dernier mode obtenu ("gallery", "downloaded", "denied"…).
export async function saveAllToGallery(items = []) {
  let saved = 0, how = null;
  for (const it of items) {
    try {
      how = await saveMediaToGallery({ url: it.url, kind: it.kind || "image" });
      if (how === "denied" || how === "cancelled") break;
      saved += 1;
    } catch { how = "error"; }
  }
  return { saved, total: items.length, how };
}

const isCancel = (e) => (e && e.name) === "AbortError" || /cancel|abort/i.test(String(e?.message || e || ""));

// Partage la photo / vidéo ELLE-MÊME (fichier) + le lien en légende : app native → feuille du système (WhatsApp affiche
// la photo avec le lien dessous) ; web avec partage de fichiers → idem ; sinon (PC) → lien seul.
// Retourne "shared" | "cancelled" | "link".
export async function shareMediaFile({ url, kind = "image", link = "", title = "Yalola" } = {}) {
  if (!url) throw new Error("shareMediaFile: url manquante");
  url = resolveMediaUrl(url);
  if (Capacitor.isNativePlatform()) {
    const blob = await fetchMediaBlob(url);
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
    const path = `${mediaBaseName(kind)}.${mediaExt(url, blob.type, kind)}`;
    await Filesystem.writeFile({ path, data: String(dataUrl).split(",")[1], directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    try { await Share.share({ title, text: link || undefined, files: [uri] }); return "shared"; } catch (e) { if (isCancel(e)) return "cancelled"; throw e; }
  }
  if (typeof navigator !== "undefined" && navigator.share && navigator.canShare) {
    try {
      const blob = await fetchMediaBlob(url);
      const file = new File([blob], `${mediaBaseName(kind)}.${mediaExt(url, blob.type, kind)}`, { type: blob.type || undefined });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: link || undefined, title });
        return "shared";
      }
    } catch (e) { if (isCancel(e)) return "cancelled"; }
  }
  const { shareUrl } = await import("@/lib/share");
  await shareUrl({ title, url: link || url });
  return "link";
}
